import { motion } from "framer-motion";
import { Atom, FlaskConical, Dna, Sigma, ArrowRight, Clock, GraduationCap, Sparkles, CheckCircle2 } from "lucide-react";
import { PageHero, CTASection } from "@/components/Bits";
import { FloatingQuote } from "@/components/FloatingQuote";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/data/content";

const SKILLS = ["Logical reasoning", "Critical thinking", "Creativity", "Data interpretation", "Experimentation", "Decision-making", "Collaboration", "Scientific inquiry"];
const CHAIN = ["STEM", "Innovation", "Technology", "Industries", "Employment", "National Development"];
const FIELDS = [
  { t: "Clean Energy",             img: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=900&auto=format&fit=crop&q=70" },
  { t: "Biotechnology",            img: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=900&auto=format&fit=crop&q=70" },
  { t: "Artificial Intelligence",  img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=900&auto=format&fit=crop&q=70" },
  { t: "Advanced Manufacturing",   img: "https://images.unsplash.com/photo-1565043666747-69f6646db940?w=900&auto=format&fit=crop&q=70" },
];

const FUNDAMENTAL_SUBJECTS = [
  {
    icon: Atom,
    hex: "#2E6BB0",
    t: "Physics",
    d: "Enables developments in energy, electronics, communication, transport, space science and computing.",
    img: "https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa?w=900&auto=format&fit=crop&q=70",
  },
  {
    icon: FlaskConical,
    hex: "#1E9E8A",
    t: "Chemistry",
    d: "Supports medicines, materials, manufacturing, agriculture, energy storage and environmental solutions.",
    img: "https://images.unsplash.com/photo-1554475901-4538ddfbccc2?w=900&auto=format&fit=crop&q=70",
  },
  {
    icon: Dna,
    hex: "#E14C7A",
    t: "Biology",
    d: "Drives healthcare, biotechnology, genetics, food security and life sciences.",
    img: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=900&auto=format&fit=crop&q=70",
  },
  {
    icon: Sigma,
    hex: "#F0A831",
    t: "Mathematics",
    d: "The language of science — foundation of engineering, economics, computing, AI and data analysis.",
    img: "https://images.unsplash.com/photo-1509228468518-180dd4864904?w=900&auto=format&fit=crop&q=70",
  },
];

const CREATOR_MODEL_POINTS = [
  "Develop each concept systematically",
  "Perform experiments instead of merely reading about them",
  "Use scientific instruments and digital sensors",
  "Observe, measure and analyse real phenomena",
  "Connect mathematics with scientific applications",
  "Solve curriculum-based, advanced and real-life problems",
  "Build readiness for research, innovation and competitive examinations",
];

const CONCEPT_LAB_FLOW = ["Observe", "Experiment", "Measure", "Analyse", "Verify", "Apply", "Solve"];

const PBL_RESEARCH_POINTS = [
  "Project/Problem-Based Learning (PBL) can provide an effective model for STEM school reform when implemented with fidelity.",
  "The U.S. National Academies' Rising Above the Gathering Storm (2007) urged states to develop statewide specialty STEM high schools to widen the STEM pipeline — with PBL as the recommended instructional core.",
  "The Texas T-STEM Academy initiative (2010) adopted problem- and project-based learning as its primary instructional strategy — evidence that PBL scales as a system-wide STEM pedagogy, not just a classroom activity.",
  "PBL is well suited as the primary pedagogy for STEM learning: students investigate real problems, apply concepts across disciplines, and build the analytical habits that college and industry demand.",
  "When implemented with high fidelity, PBL improves outcomes even in low-performing school districts — making it a research-backed lever for school improvement.",
];

export default function Stem() {
  return (
    <div data-testid="stem-page">
      <FloatingQuote />
      <PageHero
        n="03"
        label="STEM Education"
        lines={["STEM Education —", "The Foundation of Innovation,", "Entrepreneurship & National Development"]}
        intro="The future prosperity of every nation depends on its ability to create new knowledge, innovative technologies, and globally competitive enterprises."
      />

      {/* Value chain infographic */}
      <section className="border-t border-white/10 bg-[#070d18]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="◆">The Value Chain</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-stretch gap-4 md:gap-2 mt-12">
            {CHAIN.map((step, i) => {
              const colors = ["text-c-blue", "text-c-teal", "text-c-amber", "text-c-violet", "text-c-rose", "text-c-green"];
              return (
              <Reveal key={step} delay={i * 0.08} className="flex-1">
                <div className="h-full border border-white/10 p-6 hover:border-accent transition-colors group">
                  <span className={`font-mono-label text-xs ${colors[i % colors.length]}`}>{`0${i + 1}`}</span>
                  <p className="font-display text-xl md:text-2xl text-white mt-4 group-hover:text-accent transition-colors">{step}</p>
                </div>
              </Reveal>
            );})}
          </div>
          <p className="text-white/50 mt-8 max-w-3xl leading-relaxed">
            Nations that lead in research shape the future of the world. STEM is the engine that converts curiosity into innovation, and innovation into industries, employment and prosperity.
          </p>
        </div>
      </section>

      {/* Why + purpose */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-16">
          <Reveal>
            <SectionLabel n="01">Why nations invest in STEM</SectionLabel>
            <p className="text-white/65 text-lg leading-relaxed mt-6">
              Nearly every sector — from everyday household products to complex industrial systems — depends on continuous breakthroughs in Science and Technology. Investment in STEM is investment in a nation's capacity to solve its own problems.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <SectionLabel n="02">The true purpose of STEM</SectionLabel>
            <p className="text-white/65 text-lg leading-relaxed mt-6">
              The purpose is not to memorise formulae, but to build thinkers who can question, investigate, analyse and apply. STEM education must produce problem solvers, not merely degree holders.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 03 · Strengthening STEM Through Fundamental Sciences */}
      <section className="border-t border-white/10" data-testid="stem-fundamentals">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="03">Strengthening STEM Through Fundamental Sciences</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-5xl font-light">
            Physics, Chemistry, Biology and Mathematics — <span className="text-accent">at the centre of learning</span>.
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-8 max-w-6xl">
            <p className="text-white/70 leading-relaxed">
              Almost every major advancement in <span className="text-white">technology, medicine, engineering, agriculture, energy, communication and industry</span> originates from research in Physics, Chemistry, Biology or Mathematics. These subjects explain the natural world, provide the foundations of scientific discovery and make technological development possible.
            </p>
            <p className="text-white/70 leading-relaxed">
              Together, these subjects influence <span className="text-white">national development, economic growth and the quality of human life</span>. Strong fundamental sciences build strong technologies, strong economies and a stronger future.
            </p>
          </div>

          {/* Four fundamental subject cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14" data-testid="stem-four-subjects">
            {FUNDAMENTAL_SUBJECTS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.t} delay={(i % 4) * 0.06} className="rounded-2xl overflow-hidden border border-white/10 bg-[#050B14] group hover:border-accent/60 transition-colors">
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <motion.img
                      src={s.img}
                      alt={s.t}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      initial={{ scale: 1.12 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.1 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent" />
                  </div>
                  <div className="p-6">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center border" style={{ borderColor: s.hex, backgroundColor: `${s.hex}22` }}>
                      <Icon className="w-5 h-5" style={{ color: s.hex }} />
                    </div>
                    <div className="font-display text-2xl text-white mt-4 tracking-tight">{s.t}</div>
                    <p className="text-white/60 text-sm leading-relaxed mt-3">{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 04 · STEM requires more than limited classroom time */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="stem-time">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-4">
            <SectionLabel n="04">Give STEM the Time It Deserves</SectionLabel>
            <div className="mt-6 inline-flex items-center gap-3 border border-white/15 rounded-full px-4 py-2">
              <Clock className="w-4 h-4 text-accent" />
              <span className="font-mono-label text-[11px] tracking-label uppercase text-white/80">The Time Problem</span>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-8">
            <h3 className="font-display text-2xl md:text-4xl text-white tracking-tight leading-tight font-light">
              Science needs more than a few periods a week.
            </h3>
            <p className="text-white/70 leading-relaxed mt-6">
              Despite their importance, science subjects are often introduced in the earlier grades as a <span className="text-white">single combined subject</span> with limited teaching time. Physics, Chemistry and Biology may share only a few periods — leaving insufficient time for experiments, investigation, discussion and meaningful problem-solving.
            </p>
            <p className="text-white/70 leading-relaxed mt-4">
              When science is taught mainly through textbooks and brief explanations, students may remember information but often do not <span className="text-white">develop the concepts</span> required for advanced study and practical application.
            </p>
            <div className="mt-8 rounded-2xl border-l-4 border-accent border-t border-r border-b border-white/10 bg-[#050B14] p-6 md:p-7">
              <p className="font-display text-xl md:text-2xl text-white leading-snug tracking-tight">
                Quality STEM education needs <span className="text-accent">time</span> for students to observe, experiment, measure, analyse, question and solve problems.
              </p>
              <p className="text-white/60 leading-relaxed mt-3 text-sm">
                Schools must therefore give greater academic time and importance to fundamental sciences and mathematics.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 05 · THECREATOR STEM Learning Model — with school photo */}
      <section className="border-t border-white/10" data-testid="stem-creator-model">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-white/10">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/cp6pxwka_Neha.webp"
                alt="Neha — THECREATOR student conducting a chemistry experiment"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.08} className="md:col-span-7">
            <SectionLabel n="05">THECREATOR STEM Learning Model</SectionLabel>
            <h3 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">
              STEM learning begins early and becomes progressively <span className="text-accent">deeper</span>.
            </h3>
            <p className="text-white/70 leading-relaxed mt-6">
              Primary students spend substantial time developing <span className="text-white">scientific curiosity, mathematical thinking, observation and experimentation</span>. From Grade 6 onwards, Physics, Chemistry, Biology and Mathematics are treated as separate, full-valued subjects — each with dedicated time, specialist teaching, appropriate apparatus, laboratory experience and structured problem-solving.
            </p>
            <div className="mt-8 rounded-xl border border-white/10 bg-[#070d18] p-6">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">This Enables Students to</div>
              <ul className="grid sm:grid-cols-2 gap-2 mt-4">
                {CREATOR_MODEL_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-white/75 text-sm leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 06 · Every classroom as a Concept Lab — the 7-step flow */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="stem-classroom-flow">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="06">STEM at THECREATOR Education</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            PBL-TDLE: A <span className="text-accent">research-based learning framework</span> for STEM subjects.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            At THECREATOR Education, STEM is not taught as a collection of facts — it is practised as a discipline. The research-based PBL-TDLE framework governs how every STEM concept is learned: teachers introduce it through live demonstrations, students develop it through experiments, investigation and verification in the Concept Lab, and finally apply it to real problems — the same cycle a working scientist follows.
          </p>

          <div className="mt-14 flex flex-wrap items-center justify-center gap-3" data-testid="stem-flow-chips">
            {CONCEPT_LAB_FLOW.map((step, i) => {
              const colors = ["text-c-blue", "text-c-teal", "text-c-amber", "text-c-violet", "text-c-rose", "text-c-green", "text-accent"];
              return (
                <div key={step} className="flex items-center gap-3">
                  <Reveal delay={i * 0.05}>
                    <span className={`inline-flex items-center gap-2 border border-white/15 rounded-full px-5 py-3 font-mono-label text-[11px] tracking-label uppercase ${colors[i % colors.length]} bg-[#050B14]`}>
                      {step}
                    </span>
                  </Reveal>
                  {i < CONCEPT_LAB_FLOW.length - 1 && <ArrowRight className="w-4 h-4 text-white/30" />}
                </div>
              );
            })}
          </div>

          <p className="text-white/60 leading-relaxed mt-12 max-w-3xl italic">
            Concepts become clearer, deeper and longer-lasting because students have <span className="not-italic text-white">personally investigated the evidence behind them</span>.
          </p>
        </div>
      </section>

      {/* 07 · Preparing students + Developing scientists — closing */}
      <section className="border-t border-white/10" data-testid="stem-pathways">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-10">
          <Reveal className="rounded-2xl border border-white/10 bg-[#070d18] p-8 md:p-10">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-5 h-5 text-accent" />
              <span className="font-mono-label text-[11px] tracking-label uppercase text-accent">Advanced Pathways</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-white mt-4 tracking-tight leading-tight font-light">
              Preparing students for advanced STEM pathways.
            </h3>
            <p className="text-white/70 leading-relaxed mt-5">
              Providing greater time for Physics, Chemistry, Biology and Mathematics from an early stage prepares students for advanced studies in <span className="text-white">engineering, medicine, pure sciences, biotechnology, mathematics, computing, research and emerging technologies</span>.
            </p>
            <p className="text-white/60 leading-relaxed mt-4">
              It also builds the foundation for <span className="text-white">JEE, NEET, Cambridge, AP, AMC, BPhO and international Science Olympiads</span> — so students develop problem-solving continuously within the school system, without depending entirely on external coaching later.
            </p>
          </Reveal>

          <Reveal delay={0.08} className="rounded-2xl border border-white/10 bg-[#070d18] p-8 md:p-10">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-accent" />
              <span className="font-mono-label text-[11px] tracking-label uppercase text-accent">Scientists &amp; Innovators of Tomorrow</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-white mt-4 tracking-tight leading-tight font-light">
              Strong schools build strong nations.
            </h3>
            <p className="text-white/70 leading-relaxed mt-5">
              A country cannot become scientifically and technologically strong without strong foundations in its schools. World-class laboratories, industries and research institutions require students who understand <span className="text-white">fundamental sciences</span> and can use that knowledge to investigate problems and create solutions.
            </p>
            <p className="text-white/60 leading-relaxed mt-4">
              THECREATOR Education therefore places Physics, Chemistry, Biology and Mathematics at the heart of the curriculum — with more time, better apparatus, hands-on experiments and progressive problem-solving.
            </p>
          </Reveal>
        </div>

        <div className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24">
          <Reveal className="border-l-4 border-accent bg-[#050B14] rounded-2xl p-8 md:p-10 max-w-4xl">
            <p className="font-display text-2xl md:text-4xl text-white leading-tight tracking-tight">
              Strong fundamental sciences build <span className="text-accent">strong technologies</span>, strong economies and a <span className="text-accent">stronger future</span>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Skills */}
      <section className="border-t border-white/10 bg-[#070d18]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="07">Skills every future innovator needs</SectionLabel>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mt-10 border border-white/10">
            {SKILLS.map((s, i) => (
              <Reveal key={s} delay={i * 0.05} className="bg-[#070d18] p-6 md:p-8 hover:bg-[#050B14] transition-colors">
                <span className="font-mono-label text-xs text-accent">{`0${i + 1}`.slice(-2)}</span>
                <p className="font-display text-xl md:text-2xl text-white mt-4 leading-tight">{s}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Field mosaic */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="08">From primary years to advanced problem solving</SectionLabel>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {FIELDS.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.08} className="group relative aspect-[3/4] overflow-hidden border border-white/10">
                <motion.img src={f.img} alt={f.t} className="w-full h-full object-cover" initial={{ scale: 1.15 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.2 }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 font-display text-lg text-onimg">{f.t}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Research reference — PBL significance for STEM */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="stem-pbl-research">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="09">The Research Behind PBL in STEM</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Peer-reviewed evidence: PBL is the right pedagogy for STEM school reform.
          </h2>

          <div className="grid md:grid-cols-12 gap-10 mt-14 items-start">
            <div className="md:col-span-8">
              <ol className="space-y-4" data-testid="stem-pbl-points">
                {PBL_RESEARCH_POINTS.map((p, i) => (
                  <Reveal key={i} delay={i * 0.06} className="flex items-start gap-4 border border-white/10 rounded-xl p-5 bg-[#050B14]">
                    <span className="font-mono-label text-[11px] tracking-label uppercase text-accent shrink-0 mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-white/80 leading-relaxed">{p}</p>
                  </Reveal>
                ))}
              </ol>
            </div>

            <aside className="md:col-span-4 md:sticky md:top-28 rounded-2xl border-l-4 border-accent border-t border-r border-b border-white/10 bg-[#050B14] p-6 md:p-7" data-testid="stem-pbl-citation">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Cited Reference</div>
              <p className="font-display text-lg md:text-xl text-white mt-3 leading-snug">
                The Impact of PBL as a STEM School Reform Model.
              </p>
              <p className="text-white/70 leading-relaxed mt-4 text-sm">
                Odell, M. R. L., Kennedy, T. J., &amp; Stocks, E. (University of Texas at Tyler).
              </p>
              <p className="text-white/55 leading-relaxed mt-3 text-sm italic">
                Special Issue: Unpacking the Role of Assessment in Problem- and Project-Based Learning. <span className="not-italic">The Interdisciplinary Journal of Problem-based Learning.</span>
              </p>
              <p className="text-white/50 leading-relaxed mt-4 text-xs">
                Keywords: problem-based learning, project-based learning, fidelity, STEM, project-based instruction, evaluation, PBL.
              </p>
            </aside>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
